import { label, notifyIcon, number, quote, requestMethod } from "../../config/index";
import config from "../../env.config";
import { fetch } from "../../shared/services/api.service";
import { getNotification } from "../../utils/common";
import { checkApiSuccess } from "../../utils/index";

const CONTENT_MANAGER_API = `${config.BASE_URL.BASE_API_URL}/content`;

/**gives the respective message for the given action */
const getCmsActionMessage = (action, data, message) => {
  const dataArray = Array.isArray(data) ? data : [data];
  if (!dataArray.length) return;
  const [{ type, mimeType }] = dataArray;

  const isFolder = type === label.FOLDER;
  const isDoc = mimeType === label.DOX_MIME_TYPE;
  const isMultiple = action === "add" && dataArray.length > number.ONE;

  const baseMessages = {
    add: isMultiple ? quote.NEW_FILES_ADDED : isFolder ? quote.NEW_FOLDER_ADDED : isDoc ? quote.NEW_DOC_ADDED : quote.NEW_FILE_ADDED,
    update: isFolder ? quote.FOLDER_UPDATED : isDoc ? quote.DOC_UPDATED : quote.FILE_UPDATED,
    delete: isFolder ? quote.FOLDER_DELETE : isDoc ? quote.DOC_DELETED : quote.FILE_DELETED,
    restore: isFolder ? quote.FOLDER_RESTORED : isDoc ? quote.DOC_RESTORED : quote.FILE_RESTORED,
    permanentDelete: isFolder ? quote.FOLDER_PERMANENT_DELETE : isDoc ? quote.DOC_PERMANENT_DELETE : quote.DOC_PERMANENT_DELETE,
    copy: isFolder ? quote.FOLDER_COPIED : isDoc ? quote.DOC_COPIED : quote.FILE_COPIED,
    default: message,
  };

  return getNotification(baseMessages[action] || baseMessages.default, notifyIcon.SUCCESS_ICON);
};

/**
 * API call for fetching organization content attachments
 * @param {Object} { parentId, companyId }
 * @returns organization content attachments
 * @author Muskan Thakur
 */
export const getContentAttachments = async ({ parentId, projectId, userId, page = 1, sortBy, sortOrder = -1 }) => {
  const requestConfig = {
      method: requestMethod.GET,
      url: `${CONTENT_MANAGER_API}/getContentAttachments`,
      params: { parentId, projectId, userId, page, sortBy, sortOrder },
    },
    response = await fetch(requestConfig, true, false);
  if (checkApiSuccess(response)) {
    return response.data.data;
  }
};

/**
 * API call for fetching organization content attachments marked as favorite
 * @param {Object} companyId
 * @returns organization content attachments marked as favorite
 * @author Dimple Sahota
 */
export const getFavoriteContentAttachments = async ({ userId }) => {
  const requestConfig = {
      method: requestMethod.GET,
      url: `${CONTENT_MANAGER_API}/getFavoriteContentAttachments`,
      params: { userId: userId },
    },
    response = await fetch(requestConfig, true, false);
  if (checkApiSuccess(response)) {
    return response.data.data;
  }
};

/**
 * API call for updating organization content attachments sort order marked as favorite
 * @param {Object} companyId
 * @returns organization content attachments marked as favorite
 * @author Dimple Sahota
 */
export const updateFavoriteSortOrder = async ({ userId, orderedAttachmentIds }) => {
  const requestConfig = {
    method: requestMethod.PUT,
    url: `${CONTENT_MANAGER_API}/updateFavoriteSortOrder`,
    data: {
      userId,
      orderedAttachmentIds,
    },
  };

  const response = await fetch(requestConfig, true, false);

  if (checkApiSuccess(response)) {
    return response.data.data;
  }
};

/**
 * API call for fetching organization content attachments count
 * @param {Object} companyId
 * @returns organization content attachments count
 * @author Muskan Thakur
 */
export const getContentAttachmentsCount = async ({ projectId }) => {
  const requestConfig = {
      method: requestMethod.GET,
      url: `${CONTENT_MANAGER_API}/getContentAttachmentsCount`,
      params: { projectId: projectId },
    },
    response = await fetch(requestConfig, false, false);
  if (checkApiSuccess(response)) {
    return response.data.data.count;
  }
};

/**
 * API call for adding organization content attachments
 * @param {Object} (It consists of payload, userId, companyId)
 * @author Muskan Thakur
 */
export const addContentAttachments = async ({ payload, userId, companyId, projectId }) => {
  const requestConfig = {
    method: requestMethod.POST,
    url: `${CONTENT_MANAGER_API}/addContentAttachments`,
    params: { userId, companyId, projectId },
    data: payload,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response)) {
    getCmsActionMessage("add", response.data.data, response.data.message);
    return response.data.data;
  }
};

/**
 * API call for editing organization content attachments
 * @param {Object} payload
 * @author Muskan Thakur
 */
export const updateContentAttachment = async (payload) => {
  const requestConfig = {
    method: requestMethod.PUT,
    url: `${CONTENT_MANAGER_API}/updateContentAttachment `,
    data: payload,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response)) {
    getCmsActionMessage("update", response.data.data, response.data.message);
    return response.data.data;
  }
};

/**
 * API call for editing organization content attachments
 * @param {Object} payload
 */
export const getContentAttachmentById = async (id) => {
  const requestConfig = {
    method: requestMethod.GET,
    url: `${CONTENT_MANAGER_API}/getContentAttachmentById/${id}`,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response)) {
    return response.data.data;
  }
};

/**
 * API call for deleting organization content attachments
 * @param {Object} payload
 * @author Muskan Thakur
 */
export const deleteContentAttachment = async (payload) => {
  const requestConfig = {
    method: requestMethod.DELETE,
    url: `${CONTENT_MANAGER_API}/deleteContentAttachment `,
    data: payload,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response)) {
    getCmsActionMessage("delete", response.data.data, response.data.message);
    return response.data.message === quote.CONTENT_DELTED;
  }
};

/**
 * API call for getting all the deleted attachments
 * @param {Object} payload
 * @author Muskan Thakur
 */
export const getDeletedContentAttachments = async ({ projectId, userId, sortBy, sortOrder }) => {
  const requestConfig = {
    method: requestMethod.GET,
    url: `${CONTENT_MANAGER_API}/getDeletedContentAttachments`,
    params: { projectId, userId, sortBy, sortOrder },
  };
  const response = await fetch(requestConfig, false, false);
  if (checkApiSuccess(response)) {
    return response.data.data;
  }
};

/**
 * API call for restoring organization content attachments
 * @param {Object} payload
 * @author Muskan Thakur
 */
export const restoreContentAttachment = async (payload) => {
  const requestConfig = {
    method: requestMethod.PUT,
    url: `${CONTENT_MANAGER_API}/restoreContentAttachment `,
    data: payload,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response)) {
    getCmsActionMessage("restore", response.data.data, response.data.message);
    return response.data.data;
  }
};

/**
 * API call for permanently deleting organization content attachments
 * @param {Object} payload
 * @author Muskan Thakur
 */
export const permanentlyDeleteContentAttachment = async (payload) => {
  const requestConfig = {
    method: requestMethod.DELETE,
    url: `${CONTENT_MANAGER_API}/permanentlyDeleteContentAttachment  `,
    data: payload,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response)) {
    getCmsActionMessage("permanentDelete", response.data.data, response.data.message);
    return response.data.message === quote.PERMANENTLY_DELETE;
  }
};

/**
 * API call for getting all the recent attachments
 * @param {Object} payload
 * @author Muskan Thakur
 */
export const getRecentContentAttachments = async ({ projectId, userId }) => {
  const requestConfig = {
    method: requestMethod.GET,
    url: `${CONTENT_MANAGER_API}/getRecentContentAttachments`,
    params: { projectId, userId },
  };
  const response = await fetch(requestConfig, false, false);
  if (checkApiSuccess(response)) {
    return response.data.data;
  }
};

/**
 * API call for updating recent content attachments
 * @param {Object} payload
 * @author Muskan Thakur
 */
export const updateActionOnContentAttachment = async (payload) => {
  const requestConfig = {
    method: requestMethod.PUT,
    url: `${CONTENT_MANAGER_API}/updateActionOnContentAttachment `,
    data: payload,
  };
  const response = await fetch(requestConfig, false, false);
  if (checkApiSuccess(response)) {
    return response.data.data;
  }
};

/**
 * API call for moving content attachments
 * @param {Object} payload
 * @author Muskan Thakur
 */
export const moveContent = async (payload) => {
  const requestConfig = {
    method: requestMethod.PUT,
    url: `${CONTENT_MANAGER_API}/moveContent `,
    data: payload,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response)) {
    getNotification(response.data.message, notifyIcon.SUCCESS_ICON);
    return response.data.data;
  }
};

/**
 * API call for copying content attachments
 * @param {Object} payload
 * @author Muskan Thakur
 */
export const copyContent = async (payload) => {
  const requestConfig = {
    method: requestMethod.PUT,
    url: `${CONTENT_MANAGER_API}/copyContent `,
    data: payload,
  };
  const response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response)) {
    getCmsActionMessage("copy", response.data.data, response.data.message);
    return response.data.data;
  }
};

/**
 * API call for fetching the Quick Access root folder document
 * @returns Quick Access folder document with _id and name
 */
export const getQuickAccessFolder = async () => {
  const requestConfig = {
    method: requestMethod.GET,
    url: `${CONTENT_MANAGER_API}/getQuickAccessFolder`,
  };
  const response = await fetch(requestConfig, true, false);
  if (checkApiSuccess(response)) {
    return response.data.data;
  }
};



/**
 * API call for fetching organizations task assets attachments
 * @param {Object} { parentId, projectId, userId, page, limit, sortBy, sortOrder }
 * @returns organization content task attachments
 * @author Muskan Thakur
 */
export const getTaskContentAttachments = async ({ parentId, projectId, userId, page = 1, sortBy, sortOrder = -1 }) => {
  const requestConfig = {
      method: requestMethod.GET,
      url: `${CONTENT_MANAGER_API}/getTaskContentAttachments`,
      params: { parentId, projectId, userId, page, sortBy, sortOrder },
    },
    response = await fetch(requestConfig, true, false);
  if (checkApiSuccess(response)) {
    return response.data.data;
  }
};
